import type { UniversalChatMessage } from "../types/universalChatTypes";

export interface GroupedMessage {
  message: UniversalChatMessage;
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
  senderType: string;
}

/**
 * Creates a stable key for grouping by sender.
 * System messages may have an empty senderId, so fall back to clientId to avoid coalescing with non-system senders.
 */
function getSenderKey(message: UniversalChatMessage): string {
  return message.senderId || `system-${message.clientId}`;
}

/** Returns true when the provided senderType represents a system message. */
function isSystemSender(senderType: string): boolean {
  return senderType.toLowerCase() === "system";
}

/**
 * Groups consecutive messages by senderId and senderType.
 * Messages with the same senderId and senderType that appear consecutively
 * will be grouped together.
 */
export function groupMessagesBySender(
  messages: UniversalChatMessage[],
): GroupedMessage[] {
  if (!messages || messages.length === 0) return [];

  const groupedMessages: GroupedMessage[] = [];

  for (let i = 0; i < messages.length; i++) {
    const currentMessage = messages[i];
    const prevMessage = messages[i - 1];
    const nextMessage = messages[i + 1];

    const currentSenderKey = getSenderKey(currentMessage);
    const isSystem = isSystemSender(currentMessage.senderType);

    const prevSenderKey = prevMessage ? getSenderKey(prevMessage) : null;
    const nextSenderKey = nextMessage ? getSenderKey(nextMessage) : null;

    // Determine if this is the first message in a group
    const isFirstInGroup =
      isSystem ||
      !prevMessage ||
      prevSenderKey !== currentSenderKey ||
      prevMessage.senderType !== currentMessage.senderType;

    // Determine if this is the last message in a group
    const isLastInGroup =
      isSystem ||
      !nextMessage ||
      nextSenderKey !== currentSenderKey ||
      nextMessage.senderType !== currentMessage.senderType;

    groupedMessages.push({
      message: currentMessage,
      isFirstInGroup,
      isLastInGroup,
      senderType: currentMessage.senderType,
    });
  }

  return groupedMessages;
}

/**
 * Gets the avatar text for a given sender type.
 * Returns null for system messages (no avatar needed).
 */
export function getAvatarText(senderType: string): string | null {
  switch (senderType.toLowerCase()) {
    case "clinician":
      return "DR";
    case "patient":
      return "PT";
    case "system":
      return null;
    default:
      return "??";
  }
}
